import React from 'react';
import { X } from 'lucide-react';
import AddSectionModal from './AddSectionModal';

const SECTION_TYPE_COPY = {
  BILLING_SECTION: 'Use for checkout/payment counters.',
  TRIAL_SECTION: 'Use for trial rooms and fitting areas.',
  EXCHANGE_SECTION: 'Use for return/exchange processing desks.'
};

const StoreManageModals = ({
  showSections,
  isModalOpen,
  closeModal,
  formValues,
  handleFieldChange,
  floorZones,
  isZoneLoading,
  sectionFormError,
  zoneFormError,
  counterFormError,
  editFormError,
  isZoneModalOpen,
  zoneFormValues,
  zoneModalFloorZones,
  closeZoneModal,
  handleZoneFormChange,
  handleCreateZone,
  isZoneSaving,
  isSaving,
  handleSave,
  isCounterModalOpen,
  activeCounterMeta,
  counterSection,
  closeCounterModal,
  filterStatus,
  setFilterStatus,
  sortKey,
  setSortKey,
  isCounterLoading,
  counterList,
  displayCounters,
  handleToggleCounter,
  setDeleteTarget,
  deleteTarget,
  handleDeleteCounter,
  counterForm,
  handleCounterFormChange,
  counterBaseName,
  nextCounterNumber,
  handleAddCounters,
  isCounterSaving,
  isEditModalOpen,
  editSection,
  setIsEditModalOpen,
  setEditSection,
  isEditSaving,
  editFormValues,
  handleEditFormChange,
  handleUpdateSection,
  setDeleteSectionTarget,
  deleteSectionTarget,
  isDeletingSection,
  handleDeleteSection,
  sectionCreateConflict,
  isSectionCreateConflictSaving,
  closeSectionCreateConflict,
  handleConfirmSectionCreateConflict,
  isGlobalRequestLoading,
  editFloorZones = []
}) => (
  <>
    <AddSectionModal open={showSections && isModalOpen}>
      <div className="zone-modal-overlay">
        <div className="zone-modal">
          <div className="zone-modal-header">
            <div>
              <h3>Add New Section</h3>
              <p>Create a new counter or room for your store.</p>
            </div>
            <button className="btn ghost small" onClick={closeModal}>
              <X size={14} />
            </button>
          </div>
          <div className="zone-modal-body two-col">
            <div className="manage-input-group full-width">
              <label>Section Type</label>
              <div className="section-type-select-shell">
                <select
                  className="analytics-select manage-select section-type-select"
                  value={formValues.sectionType}
                  onChange={(event) => handleFieldChange('sectionType', event.target.value)}
                >
                  <option value="" disabled hidden>Select a type...</option>
                  <option value="BILLING_SECTION">Billing Section</option>
                  <option value="TRIAL_SECTION">Trial Section</option>
                  <option value="EXCHANGE_SECTION">Exchange Section</option>
                </select>
              </div>
              <p className="section-type-hint">
                {SECTION_TYPE_COPY[formValues.sectionType] || 'Choose a section type to continue.'}
              </p>
            </div>
            <div className="manage-input-group">
              <label>Floor Number</label>
              <div className="section-wing-select-shell">
                <select
                  className="analytics-select manage-select"
                  value={formValues.floorNumber}
                  onChange={(event) => handleFieldChange('floorNumber', event.target.value)}
                >
                  <option value="" disabled hidden>Select floor...</option>
                  {[...Array(16).keys()].map((val) => (
                    <option key={val} value={val}>{val === 0 ? 'Ground Floor' : `Floor ${val}`}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="manage-input-group">
              <label>Grace Period (Mins)</label>
              <input
                type="number"
                className="analytics-select"
                min="1"
                value={formValues.gracePeriodMins}
                onChange={(event) => handleFieldChange('gracePeriodMins', event.target.value)}
                placeholder="e.g. 10"
              />
            </div>
            <div className="manage-input-group">
              <label>Section Windows</label>
              <input
                type="number"
                className="analytics-select"
                min="1"
                value={formValues.sectionWindows}
                onChange={(event) => handleFieldChange('sectionWindows', event.target.value)}
                placeholder="e.g. 5"
              />
            </div>
            <div className="manage-input-group">
              <label>Section Wing</label>
              <div className="section-wing-select-shell">
                <select
                  className="analytics-select manage-select section-wing-select"
                  value={formValues.sectionWing}
                  onChange={(event) => handleFieldChange('sectionWing', event.target.value)}
                  disabled={!formValues.floorNumber || isZoneLoading}
                >
                  <option value="" disabled hidden>
                    {isZoneLoading ? 'Loading zones...' : 'Select a zone...'}
                  </option>
                  {floorZones.map((zone) => (
                    <option key={`${zone.floorNumber}-${zone.zoneName}`} value={zone.zoneName}>
                      {zone.zoneName}
                    </option>
                  ))}
                </select>
              </div>
              <p className="section-wing-hint">
                {!formValues.floorNumber
                  ? 'Enter a floor number first to load zones.'
                  : isZoneLoading
                    ? 'Loading available zones for this floor.'
                    : `Choose from ${floorZones.length} registered zone${floorZones.length === 1 ? '' : 's'} on this floor.`}
              </p>
              {!isZoneLoading && floorZones.length === 0 && formValues.floorNumber && (
                <p className="zone-field-hint">
                  No zones found for this floor. Use Add Zone from the header before creating a section.
                </p>
              )}
            </div>
          </div>
          <div className="zone-modal-actions">
            <button className="btn ghost" onClick={closeModal} disabled={isSaving}>Cancel</button>
            <button className="btn primary" onClick={handleSave} disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Create Section'}
            </button>
          </div>
          {sectionFormError ? (
            <p className="field-error" role="alert">{sectionFormError}</p>
          ) : null}
        </div>
      </div>
    </AddSectionModal>

    {showSections && isZoneModalOpen && (
      <div className="zone-modal-overlay">
        <div className="zone-modal zone-modal-zone-register">
          <div className="zone-modal-header">
            <div>
              <h3>Add New Zone</h3>
              <p>Register a floor-wise zone before creating sections.</p>
            </div>
            <button className="btn ghost small" onClick={closeZoneModal} disabled={isZoneSaving}>
              <X size={14} />
            </button>
          </div>
          <div className="zone-modal-body two-col">
            <div className="manage-input-group">
              <label>Floor Number</label>
              <div className="section-wing-select-shell">
                <select
                  className="analytics-select manage-select"
                  value={zoneFormValues.floorNumber}
                  onChange={(event) => handleZoneFormChange('floorNumber', event.target.value)}
                >
                  <option value="" disabled hidden>Select floor...</option>
                  {[...Array(16).keys()].map((val) => (
                    <option key={val} value={val}>{val === 0 ? 'Ground Floor' : `Floor ${val}`}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="manage-input-group">
              <label>Zone Name</label>
              <input
                type="text"
                className="analytics-select"
                value={zoneFormValues.zoneName}
                onChange={(event) => handleZoneFormChange('zoneName', event.target.value)}
                placeholder="e.g. Near the Entrance"
              />
            </div>
            <div className="manage-input-group full-width">
              <label>Existing Zones (Selected Floor)</label>
              {zoneModalFloorZones.length === 0 ? (
                <p className="zone-field-hint">No zones found for this floor.</p>
              ) : (
                <div className="zone-existing-list">
                  {zoneModalFloorZones.map((zone) => (
                    <span key={`${zone.floorNumber}-${zone.zoneName}`} className="zone-existing-chip">
                      {zone.zoneName}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="zone-modal-actions">
            <button className="btn ghost" onClick={closeZoneModal} disabled={isZoneSaving}>Cancel</button>
            <button className="btn primary" onClick={handleCreateZone} disabled={isZoneSaving}>
              {isZoneSaving ? 'Adding...' : 'Add New Zone'}
            </button>
          </div>
          {zoneFormError ? (
            <p className="field-error" role="alert">{zoneFormError}</p>
          ) : null}
        </div>
      </div>
    )}

    {showSections && isCounterModalOpen && (
      <div className="zone-modal-overlay">
        <div className="zone-modal counter-modal">
          <div className="zone-modal-header">
            <div>
              <h3>{activeCounterMeta.manageLabel}</h3>
              <p className="text-muted">{counterSection?.sectionName || 'Section'}</p>
            </div>
            <button className="btn ghost small" onClick={closeCounterModal}>
              <X size={14} />
            </button>
          </div>
          <div className="counter-body">
            <div className="counter-list">
              <div className="counter-toolbar">
                <div className="manage-input-group">
                  <label>Filter</label>
                  <div className="counter-select">
                    <select
                      className="analytics-select manage-select counter-select-input"
                      value={filterStatus}
                      onChange={(event) => setFilterStatus(event.target.value)}
                    >
                      <option value="ALL">All</option>
                      <option value="ACTIVE">Active</option>
                      <option value="HIDDEN">Hidden</option>
                    </select>
                  </div>
                </div>
                <div className="manage-input-group">
                  <label>Sort</label>
                  <div className="counter-select">
                    <select
                      className="analytics-select manage-select counter-select-input"
                      value={sortKey}
                      onChange={(event) => setSortKey(event.target.value)}
                    >
                      <option value="NAME">Name</option>
                      <option value="STATUS">Status</option>
                    </select>
                  </div>
                </div>
              </div>
              {!isCounterLoading && counterList.length === 0 && (
                <div className="counter-empty">No {activeCounterMeta.unitLabel.toLowerCase()} yet.</div>
              )}
              {!isCounterLoading && counterList.length > 0 && displayCounters.length === 0 && (
                <div className="counter-empty">No {activeCounterMeta.unitLabel.toLowerCase()} match this filter.</div>
              )}
              {!isCounterLoading && displayCounters.length > 0 && displayCounters.map((counter) => (
                <div className="counter-row" key={counter.sectionUuid}>
                  <div>
                    <div className="counter-title">{counter.sectionName || activeCounterMeta.singleUnitLabel}</div>
                    <div className={`counter-status ${counter.rowStatus === 1 ? 'active' : 'hidden'}`}>
                      {counter.rowStatus === 1 ? 'Active' : 'Hidden'}
                    </div>
                  </div>
                  <div className="counter-actions">
                    <button
                      className="btn ghost small"
                      onClick={() => handleToggleCounter(counter)}
                      title="select hide /unhide option when the counter is down"
                    >
                      {counter.rowStatus === 1 ? 'Hide' : 'Unhide'}
                    </button>
                    <button
                      className="btn ghost small danger"
                      onClick={() => setDeleteTarget(counter)}
                      title="Consider deleting at your own risk."
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="counter-form">
              <h4>Add {activeCounterMeta.unitLabel}</h4>
              <div className="counter-form-grid">
                <div className="manage-input-group">
                  <label>{activeCounterMeta.singleUnitLabel} naming</label>
                  <div className="counter-auto-name">
                    <strong>Auto:</strong> {counterBaseName || activeCounterMeta.singleUnitLabel} {nextCounterNumber}
                    {Number(counterForm.count) > 1 ? ` ... ${counterBaseName || activeCounterMeta.singleUnitLabel} ${nextCounterNumber + Number(counterForm.count) - 1}` : ''}
                  </div>
                </div>
                <div className="manage-input-group">
                  <label>How many?</label>
                  <div className="counter-select">
                    <select
                      className="analytics-select manage-select counter-select-input"
                      value={counterForm.count}
                      onChange={(event) => handleCounterFormChange('count', event.target.value)}
                    >
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((value) => (
                        <option key={value} value={value}>{value}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div className="counter-form-actions">
                <button className="btn primary small" onClick={handleAddCounters} disabled={isCounterSaving}>
                  {isCounterSaving ? 'Adding...' : `Add ${activeCounterMeta.unitLabel}`}
                </button>
              </div>
              {counterFormError ? (
                <p className="field-error" role="alert">{counterFormError}</p>
              ) : null}
            </div>
          </div>
          {deleteTarget && (
            <div className="counter-confirm">
              <div className="counter-confirm-card">
                <h4>Delete {activeCounterMeta.singleUnitLabel}?</h4>
                <p>Consider deleting at your own risk.</p>
                <div className="counter-confirm-actions">
                  <button className="btn ghost small" onClick={() => setDeleteTarget(null)}>Cancel</button>
                  <button className="btn primary small danger" onClick={handleDeleteCounter}>Delete</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    )}

    {showSections && isEditModalOpen && editSection && (
      <div className="zone-modal-overlay">
        <div className="zone-modal">
          <div className="zone-modal-header">
            <div>
              <h3>Edit Section</h3>
              <p>Update details for {editSection.sectionName || 'this section'}.</p>
            </div>
            <button className="btn ghost small" onClick={() => { setIsEditModalOpen(false); setEditSection(null); }} disabled={isEditSaving}>
              <X size={14} />
            </button>
          </div>
          <div className="zone-modal-body two-col">
            <div className="manage-input-group full-width">
              <label>Section Name</label>
              <input
                type="text"
                className="analytics-select"
                value={editFormValues.sectionName}
                onChange={(e) => handleEditFormChange('sectionName', e.target.value)}
                placeholder="Section name"
              />
            </div>
            <div className="manage-input-group full-width">
              <label>Section Type</label>
              <div className="section-type-select-shell">
                <select
                  className="analytics-select manage-select section-type-select"
                  value={editFormValues.sectionType}
                  onChange={(e) => handleEditFormChange('sectionType', e.target.value)}
                >
                  <option value="" disabled hidden>Select a type...</option>
                  <option value="BILLING_SECTION">Billing Section</option>
                  <option value="TRIAL_SECTION">Trial Section</option>
                  <option value="EXCHANGE_SECTION">Exchange Section</option>
                </select>
              </div>
            </div>
            <div className="manage-input-group">
              <label>Floor Number</label>
              <div className="section-wing-select-shell">
                <select
                  className="analytics-select manage-select"
                  value={editFormValues.floorNumber}
                  onChange={(e) => handleEditFormChange('floorNumber', e.target.value)}
                >
                  <option value="" disabled hidden>Select floor...</option>
                  {[...Array(16).keys()].map((val) => (
                    <option key={val} value={val}>{val === 0 ? 'Ground Floor' : `Floor ${val}`}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="manage-input-group">
              <label>Grace Period (Mins)</label>
              <input
                type="number"
                className="analytics-select"
                min="1"
                value={editFormValues.gracePeriodMins}
                onChange={(e) => handleEditFormChange('gracePeriodMins', e.target.value)}
                placeholder="e.g. 10"
              />
            </div>
            <div className="manage-input-group">
              <label>Section Windows</label>
              <input
                type="number"
                className="analytics-select"
                min="1"
                value={editFormValues.sectionWindows}
                onChange={(e) => handleEditFormChange('sectionWindows', e.target.value)}
                placeholder="e.g. 5"
              />
            </div>
            <div className="manage-input-group">
              <label>Section Wing</label>
              <div className="section-wing-select-shell">
                <select
                  className="analytics-select manage-select section-wing-select"
                  value={editFormValues.sectionWing}
                  onChange={(e) => handleEditFormChange('sectionWing', e.target.value)}
                  disabled={!editFormValues.floorNumber || isZoneLoading}
                >
                  <option value="" disabled hidden>
                    {isZoneLoading ? 'Loading zones...' : 'Select a zone...'}
                  </option>
                  {editFloorZones.map((zone) => (
                    <option key={`${zone.floorNumber}-${zone.zoneName}`} value={zone.zoneName}>
                      {zone.zoneName}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          <div className="zone-modal-actions zone-modal-actions-between">
            <button
              className="btn ghost small danger"
              onClick={() => { setIsEditModalOpen(false); setEditSection(null); setDeleteSectionTarget(editSection); }}
              disabled={isEditSaving}
            >
              Delete Section
            </button>
            <div className="zone-modal-inline-actions">
              <button className="btn ghost" onClick={() => { setIsEditModalOpen(false); setEditSection(null); }} disabled={isEditSaving}>Cancel</button>
              <button className="btn primary" onClick={handleUpdateSection} disabled={isEditSaving}>
                {isEditSaving ? 'Saving...' : 'Update Section'}
              </button>
            </div>
          </div>
          {editFormError ? (
            <p className="field-error" role="alert">{editFormError}</p>
          ) : null}
        </div>
      </div>
    )}

    {showSections && deleteSectionTarget && (
      <div className="zone-modal-overlay">
        <div className="zone-modal zone-modal-section-delete">
          <div className="zone-modal-header">
            <h3>Delete Section?</h3>
            <button className="btn ghost small" onClick={() => setDeleteSectionTarget(null)} disabled={isDeletingSection}>
              <X size={14} />
            </button>
          </div>
          <div className="section-delete-modal-copy">
            <p>
              This will permanently delete <strong>{deleteSectionTarget.sectionName || 'this section'}</strong> and all its counters.
            </p>
            <p>Consider deleting at your own risk:</p>
            <ul>
              <li>Customers currently waiting in this section queue will be removed.</li>
              <li>Any notified or active queue tokens for this section will be invalidated.</li>
              <li>This action cannot be undone.</li>
            </ul>
          </div>
          <div className="zone-modal-actions">
            <button className="btn ghost small" onClick={() => setDeleteSectionTarget(null)} disabled={isDeletingSection}>Cancel</button>
            <button className="btn primary small danger" onClick={handleDeleteSection} disabled={isDeletingSection}>
              {isDeletingSection ? 'Deleting...' : 'Delete Section'}
            </button>
          </div>
        </div>
      </div>
    )}

    {showSections && sectionCreateConflict?.open && (
      <div className="zone-modal-overlay">
        <div className="zone-modal zone-modal-section-conflict">
          <div className="zone-modal-header">
            <h3>Section Already Exists</h3>
          </div>
          <div className="section-conflict-copy">
            <p>{sectionCreateConflict.question}</p>
            <p>Choosing continue will create a new section with the same floor and zone.</p>
          </div>
          <div className="zone-modal-actions">
            <button
              className="btn ghost small"
              onClick={closeSectionCreateConflict}
              disabled={isSectionCreateConflictSaving}
            >
              Cancel
            </button>
            <button
              className="btn primary small"
              onClick={handleConfirmSectionCreateConflict}
              disabled={isSectionCreateConflictSaving}
            >
              {isSectionCreateConflictSaving ? 'Creating...' : 'Yes, Continue'}
            </button>
          </div>
        </div>
      </div>
    )}

    {isGlobalRequestLoading && (
      <div className="global-request-overlay" aria-live="polite" aria-label="Syncing data">
        <div className="global-request-loader">
          <span className="global-request-spinner" />
          <p>Syncing data...</p>
        </div>
      </div>
    )}
  </>
);

export default StoreManageModals;
